import { View, Text, TouchableOpacity } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import type { Event } from "../app/lib/types";

interface EventCardProps {
  event: Event;
  onPress?: (event: Event) => void;
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    // Force UTC — events are stored as UTC midnight, rendering in local TZ would shift back a day
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
  } catch {
    return dateStr;
  }
}

export function EventCard({ event, onPress }: EventCardProps) {
  const location = [event.city, event.state].filter(Boolean).join(", ");
  const isUpcoming = new Date(event.date) >= new Date();
  const isClosingPassed = event.closingDate && new Date(event.closingDate) < new Date();
  const isOpen = event.openingDate && new Date(event.openingDate) <= new Date() && !isClosingPassed;
  // Featured-card highlight is an explicit per-event toggle (DB column).
  // Asset presence (logo/gallery) no longer auto-promotes — we may want enhanced
  // assets without the cream/orange treatment.
  const isFeatured = !!event.isFeatured;

  return (
    <TouchableOpacity
      onPress={() => onPress?.(event)}
      activeOpacity={0.7}
      style={{
        backgroundColor: isFeatured ? "#FFFBEB" : "#ffffff",
        borderRadius: 12,
        padding: 16,
        marginBottom: 12,
        borderWidth: isFeatured ? 2 : 1,
        borderColor: isFeatured ? "#F5A623" : "#e5e7eb",
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: isFeatured ? 0.1 : 0.05,
        shadowRadius: isFeatured ? 6 : 3,
        elevation: isFeatured ? 4 : 2,
      }}
    >
      {isFeatured && (
        <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 8, gap: 4 }}>
          <Ionicons name="star" size={12} color="#F5A623" />
          <Text style={{ fontSize: 11, fontWeight: "800", color: "#92400e", letterSpacing: 0.6, textTransform: "uppercase" }}>Featured Event</Text>
        </View>
      )}
      <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Text style={{ fontSize: 15, fontWeight: "700", color: "#1B2A4A", flex: 1, marginRight: 8 }}>
          {event.name}
        </Text>
        <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
          {isClosingPassed ? (
            <View style={{ backgroundColor: "#fef2f2", paddingHorizontal: 8, paddingVertical: 3, borderRadius: 12 }}>
              <Text style={{ fontSize: 12, fontWeight: "700", color: "#dc2626" }}>Entries Closed</Text>
            </View>
          ) : isOpen ? (
            <View style={{ backgroundColor: "#d1fae5", paddingHorizontal: 8, paddingVertical: 3, borderRadius: 12 }}>
              <Text style={{ fontSize: 12, fontWeight: "700", color: "#065f46" }}>Entries Open</Text>
            </View>
          ) : isUpcoming ? (
            <View style={{ backgroundColor: "#dbeafe", paddingHorizontal: 8, paddingVertical: 3, borderRadius: 12 }}>
              <Text style={{ fontSize: 12, fontWeight: "700", color: "#1e40af" }}>Upcoming</Text>
            </View>
          ) : null}
          {event.entryFee && (
            <View style={{ backgroundColor: "#FEF3C7", paddingHorizontal: 6, paddingVertical: 2, borderRadius: 8 }}>
              <Text style={{ fontSize: 11, fontWeight: "600", color: "#92400e" }}>{event.entryFee}</Text>
            </View>
          )}
          <Ionicons name="chevron-forward" size={16} color="#9ca3af" />
        </View>
      </View>

      {event.club && event.club !== event.name ? (
        <Text style={{ fontSize: 15, color: "#6b7280", marginTop: 4 }}>{event.club}</Text>
      ) : null}

      <View style={{ flexDirection: "row", marginTop: 10, gap: 16, flexWrap: "wrap" }}>
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <Ionicons name="calendar-outline" size={13} color="#9ca3af" />
          <Text style={{ fontSize: 15, color: "#374151", marginLeft: 4 }}>
            {formatDate(event.date)}
          </Text>
        </View>
        {location ? (
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <Ionicons name="location-outline" size={13} color="#9ca3af" />
            <Text style={{ fontSize: 15, color: "#374151", marginLeft: 4 }}>
              {location}
            </Text>
          </View>
        ) : null}
      </View>
      {typeof event.distance_miles === "number" ? (
        <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "flex-end", marginTop: 6 }}>
          <Ionicons name="navigate-outline" size={13} color="#9ca3af" />
          <Text style={{ fontSize: 13, color: "#6b7280", marginLeft: 4, fontWeight: "500" }}>
            {Math.round(event.distance_miles)} mi away
          </Text>
        </View>
      ) : null}
    </TouchableOpacity>
  );
}
